import find from "lodash-es/find";
import has from "lodash-es/has";
import includes from "lodash-es/includes";
import isEqual from "lodash-es/isEqual";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import keyBy from "lodash-es/keyBy";
import keys from "lodash-es/keys";
import pull from "lodash-es/pull";
import uniq from "lodash-es/uniq";
import unset from "lodash-es/unset";
import values from "lodash-es/values";
import { action, makeAutoObservable, runInAction } from "mobx";

import { apiClient } from "../common/apiClient";
import { API_HOST } from "../common/environment";
import { logger } from "../common/logger";
import { sendRemoveItemFromFavoriteList } from "../common/tracking";
import type { BaseStoreType } from "../common/types/BaseStoreType";
import { accumulate } from "../common/utils";
import theme from "../themes/theme";
import authStore from "./auth/authStore";
import productStore from "./product/productStore";
import toastStore from "./toastStore";

export type FavoriteList = {
   id: string;
   listName: string;
   items: Record<string, number>;
   sequence: string[];
};

type FavoriteStoreType = BaseStoreType & {
   allFavoriteLists: Record<string, FavoriteList>;
   allSkus: string[];
   checkedListItems: FavoriteList["items"];
   currentFavoriteList: FavoriteList["items"];
   isFavorite(sku: string): boolean;
   addFavorite(sku: string, listId?: string, qty?: number): void;
   addMultipleFavorites(skus: string[], listId: string, items: FavoriteList["items"]): void;
   removeFavorite(sku: string, listId?: string): void;
   syncAllSkus(): void;
   getFavoriteListById(id: string): FavoriteList | undefined;
   getFavoriteListByName(name: string): FavoriteList | undefined;
   createNewListInStorage(listName: string): Promise<FavoriteList>;
   changeListNameInStorage(listId: string, newListName: string): Promise<void>;
   deleteList(listId: string): Promise<void>;
   loadFavorites(): Promise<void>;
   saveFavorites(listId: string, performSync?: boolean): Promise<void>;
};

const isAbleToAddFavorite = (list: FavoriteList | undefined) => {
   if (!authStore.isLoggedIn()) {
      logger.warn("favorites", "Attempting to add favorite without being logged in");
      return false;
   }
   if (!list) {
      logger.warn("favorites", "Unable to find list to add favorite to");
      return false;
   }
   return true;
};

const isFavoriteInList = (sku: string, listId: string) => {
   const list = favoriteStore.getFavoriteListById(listId);
   return !!list && includes(list.sequence, sku);
};

const getUrl = (customerNumber: string) => {
   return `${API_HOST}/api/auth/${theme.storeId}/favorites/${customerNumber}`;
};

const favoriteStore: FavoriteStoreType = makeAutoObservable({
   allFavoriteLists: {},
   allSkus: [],
   checkedListItems: {},
   currentFavoriteList: {},

   isFavorite: (sku) => {
      return includes(favoriteStore.allSkus, sku);
   },

   addFavorite: action((sku, listId, qty = 1) => {
      const favoriteLists = values(favoriteStore.allFavoriteLists);
      const list = isNil(listId) ? values(favoriteLists)[0] : favoriteStore.getFavoriteListById(listId);
      if (isAbleToAddFavorite(list) && !!list) {
         list.items[sku] = qty;
         list.sequence.push(sku);
         void favoriteStore.saveFavorites(list.id);
      } else if (!listId && !favoriteLists.length) {
         favoriteStore.createNewListInStorage("Mine favoritter").then((list) => {
            list.items[sku] = qty;
            list.sequence.push(sku);
            void favoriteStore.saveFavorites(list.id);
         });
      }
   }),

   addMultipleFavorites: action((skus, listId, items) => {
      const list = isNil(listId) ? values(favoriteStore.allFavoriteLists)[0] : favoriteStore.getFavoriteListById(listId);
      if (isAbleToAddFavorite(list) && !!list) {
         for (const sku of skus) {
            if (!isFavoriteInList(sku, listId)) {
               const newItem = { [sku]: items[sku] };
               list.items = { ...list.items, ...newItem };
               list.sequence.push(sku);
            } else {
               list.items[sku] = list.items[sku] + items[sku];
            }
         }
         void favoriteStore.saveFavorites(list.id);
      }
   }),

   removeFavorite: action((sku, listId) => {
      if (!authStore.isLoggedIn()) {
         logger.warn("favorites", "Attempting to remove favorite without being logged in");
         return;
      }

      if (!isNil(listId)) {
         // If listId is specified, remove SKU from specific list only
         const list = favoriteStore.getFavoriteListById(listId);
         if (!list) {
            logger.warn("favorites", "Unable to find list to remove favorite from");
            return;
         }
         unset(list.items, sku);
         pull(list.sequence, sku);
         void favoriteStore.saveFavorites(list.id, true);
         sendRemoveItemFromFavoriteList(sku, list.listName, list.sequence.indexOf(sku) + 1);
      } else {
         // If no listId is specified, remove it from all lists
         for (const list of values(favoriteStore.allFavoriteLists)) {
            if (has(list.items, sku)) {
               unset(list.items, sku);
               pull(list.sequence, sku);
               void favoriteStore.saveFavorites(list.id, false);
               sendRemoveItemFromFavoriteList(sku, list.listName, list.sequence.indexOf(sku) + 1);
            }
         }

         // Manual sync since we might have changed several lists
         favoriteStore.syncAllSkus();
      }
   }),

   syncAllSkus: action(() => {
      const allSkus = values(favoriteStore.allFavoriteLists).flatMap((fl) => keys(fl.items));
      favoriteStore.allSkus = uniq(allSkus);
   }),

   getFavoriteListById: (id) => find(favoriteStore.allFavoriteLists, { id }),

   getFavoriteListByName: (name) => {
      const favoriteList = values(favoriteStore.allFavoriteLists);
      return favoriteList.find((favorite) => favorite.listName === name);
   },

   createNewListInStorage: (listName) => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to create list before customer number is available", authStore.user);
         return Promise.reject("Not logged in, unable to create new list");
      }

      return apiClient(getUrl(customerNumber), authStore.getSessionToken())
         .query({ listName })
         .post()
         .json(
            action((list) => {
               favoriteStore.allFavoriteLists[list.id] = list;
               logger.log("favorites", "Created new list: ", listName);
               return list;
            })
         );
   },

   changeListNameInStorage: (listId: string, newListName: string) => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to change list name before customer number is available", authStore.user);
         return Promise.reject("Not logged in, unable to change list name");
      }

      return apiClient(`${getUrl(customerNumber)}/${listId}/name`, authStore.getSessionToken())
         .put(newListName)
         .json(
            action((list) => {
               favoriteStore.allFavoriteLists[list.id].listName = list.listName;
               logger.log("favorites", "Changed list name: ", newListName);
            })
         )
         .catch((res) => {
            logger.error("favorites", "Error during changing of list name", res);
            toastStore.addError("Feil under endring av navn", "Det oppsto en uforutsett feil under endring av navn på listen.", {
               context: "favorite_lists",
               text: "edit_name_error"
            });
         });
   },

   deleteList: (listId) => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to delete favorites before customer number is available", authStore.user);
         return Promise.reject("Not logged in, unabel to delete list");
      }

      return apiClient(`${getUrl(customerNumber)}/${listId}`, authStore.getSessionToken())
         .delete()
         .text(
            action(() => {
               unset(favoriteStore.allFavoriteLists, listId);
               favoriteStore.syncAllSkus();
            })
         );
   },

   loadFavorites: async () => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to load favorites before customer number is available", authStore.user);
         return Promise.reject();
      }
      logger.log("favorites", `Fetching favorites for customer number ${customerNumber}`);
      try {
         const favorites: FavoriteList[] = await apiClient(getUrl(customerNumber), authStore.getSessionToken()).get().json();
         runInAction(() => {
            for (const favorite of favorites) {
               favorite.items = Object.keys(favorite.items)
                  .map((sku) => ({ sku: productStore.replacementFor(sku) || sku, qty: favorite.items[sku] }))
                  .map((item) => ({ [item.sku]: item.qty }))
                  .reduce(accumulate, {});
               favorite.sequence = favorite.sequence.map((sku) => productStore.replacementFor(sku) || sku);
            }
            favoriteStore.allFavoriteLists = keyBy(favorites, "id");
            // Ensure sequence is unique, which should clean up old duplicates
            for (const key of Object.keys(favoriteStore.allFavoriteLists)) {
               favoriteStore.allFavoriteLists[key].sequence = uniq(favoriteStore.allFavoriteLists[key].sequence);
            }
            favoriteStore.syncAllSkus();
         });

         logger.log("favorites", "Favorites loaded: ", favoriteStore.allFavoriteLists);
      } catch (err) {
         logger.error("favorites", "Error during loading of favorites", err);
         toastStore.addError("Feil under lasting av favoritter", "Det oppsto en uforutsett feil under lasting av favoritter.", {
            context: "favorite_lists",
            text: "fetch_favorite_lists_error"
         });
      }
   },

   saveFavorites: async (listId, performSync = true) => {
      const customerNumber = authStore.currentCompany;
      if (isNil(customerNumber)) {
         logger.warn("favorites", "Attempting to save favorites before customer number is available", authStore.user);
         return Promise.reject("Not logged in, unable to save favorites");
      }

      const list = favoriteStore.getFavoriteListById(listId);
      if (isUndefined(list)) {
         logger.warn("favorites", `Unable to find favorite list with id ${listId}`);
         return Promise.reject();
      }

      if (performSync) {
         favoriteStore.syncAllSkus();
      }

      return apiClient(`${getUrl(customerNumber)}/${listId}`, authStore.getSessionToken())
         .put(list)
         .json(
            action((res) => {
               list.listName = res.listName;
               const itemsChanged = !isEqual(list.items, res.items);
               if (itemsChanged) {
                  list.items = res.items;
                  if (performSync) {
                     favoriteStore.syncAllSkus();
                  }
               }
            })
         )
         .catch((res) => {
            logger.error("favorites", "Unable to save favorites", res);
            toastStore.addError(
               "Feil under lagring",
               "Vi greide dessverre ikke å lagre endringen i favorittlister. Prøv igjen senere.",
               { context: "favorite_lists", text: "add_favorite_error" }
            );
         });
   },
   clearCompanySpecificData() {
      runInAction(() => {
         favoriteStore.allFavoriteLists = {};
         favoriteStore.syncAllSkus();
      });
   }
} satisfies FavoriteStoreType);

export default favoriteStore;
